import { SimpleGrid, useToast } from "@chakra-ui/react";
import { useState } from "react";

import { UserDto } from "doxecure-commons/doxecureClientTypes";
import { ProductType } from "doxecure-commons/doxecureTypes";
import { $ok } from "foundation-ts/commons";
import { Nullable, UUID } from "foundation-ts/types";

import { SeaShellBox } from "@/components/SeaShellBox";
import { OrdersCard } from "@/components/SubcriptionCards/OrdersCard";
import { MandateCard }  from "@components/SubcriptionCards/MandateCard";
import { PlanCard }     from "@components/SubcriptionCards/PlanCard";
import { UsersCard }    from "@components/SubcriptionCards/UsersCard";
import { Mandate } from "@components/SubcriptionCards/types";
import { config } from "@/config/config";
import { useMollie } from "@/contexts/MollieContext";
import { useOrders } from "@/hooks/useOrders";
import { usePaymentStatus } from "@/hooks/usePaymentStatus";
import useProductList from "@/hooks/useProductList";
import { useSubscription } from "@hooks/useSubscription";
import { createCBMandate, updateMandate } from "@services/users.service";
import { defaultErrorToast, defaultSuccessToast } from "@/utils/toast";
import { WaitingModal } from "@/components/Modals/WaitingModal";

interface SubscriptionTabProps {
    connectedUser?: Nullable<UserDto>;
}

export const SubscriptionTab = (props: SubscriptionTabProps) => {
    const { connectedUser } = props;
    const [isPendingPayment, setIsPendingPayment] = useState(false);
    const [pendingMessage, setPendingMessage] = useState<Nullable<string>>(null);
    const [mandate, setMandate] = useState<Nullable<Mandate>>(connectedUser?.corporations?.first()?.mandate as Mandate);
    const { data: activeSubscription, buyExtraPack, cancelDowngradingRequest, cancelTerminationRequest, requestCancelSubscription, requestDowngradRenewalToStandardPlan, startSubscriptionImmediately, upgradeToUnlimited } = useSubscription(isPendingPayment);
    const { data: orders, downloadInvoice } = useOrders();
    const { data: productsList } = useProductList();
    const { mollie, ready } = useMollie();
    const { startPendingPayment, handlePaymentError } = usePaymentStatus(activeSubscription, 
        (msg: string) => {
            setPendingMessage(msg);
            setIsPendingPayment(true);
        },
        () => {
            setPendingMessage(null);
            setIsPendingPayment(false);
        }
    );
    const toast = useToast();

    const corporation           = connectedUser?.corporations?.first();
    const isTrialPeriod         = $ok(corporation?.trialPeriod?.end);
    const pendingOrder          = orders?.find(o => o.payment?.status === "pending");
    const maxUsers              = isTrialPeriod ? corporation?.trialPeriod?.extraUsers ?? 1 : corporation?.maxUsers ?? -1;
    const productPack           = productsList?.packs.find(p => p.type === ProductType.Pack);
    const productCollaborator   = productsList?.packs.find(p => p.type === ProductType.Collaborator);
    const productUnlimited      = productsList?.subscriptions.find(p => p.type === ProductType.Subscription && !!p.isUnlimited);
    const productStandard       = productsList?.subscriptions.find(p => p.type === ProductType.Subscription && !p.isUnlimited);

    const handleCardSubmit = async (cbToken: string): Promise<void> => {
        const checkoutURL = await createCBMandate(corporation!.apid, cbToken);
        if ($ok(checkoutURL)) {
            window.location.href = checkoutURL!;
        }
    };

    const handleMandateSubmit = async (consumerAccount: string, consumerName: string): Promise<void> => {
        const corpDTO = await updateMandate(corporation!.apid, consumerName, consumerAccount);
        if ($ok(corpDTO)) { 
            setMandate(corpDTO.mandate);
            toast(defaultSuccessToast("Nous avons pris en compte votre nouveau mandat SEPA"));
        }
    };

     const handleBuyExtractPack = async () => {
        buyExtraPack.mutate(undefined, {
            onSuccess: () => {
                if ($ok(corporation?.mandate?.cardNumber)) {
                    startPendingPayment("buyPack");
                } else {
                    toast(defaultSuccessToast("Nous avons bien pris en compte votre demande d'achat d'un pack de signatures, celle-ci sera traitée dans les meilleurs délais"));
                }
            },
            onError: () => { handlePaymentError("buyPack"); }
        });
    };

    const handleCancelDowngradingRequest = async () => {
        cancelDowngradingRequest.mutate(undefined, {
            onSuccess: () => { toast(defaultSuccessToast("L'annulation de la demande de retour à l'abonnement standard a bien été prise en compte")); },
            onError: () => { toast(defaultErrorToast("Une erreur est survenue lors de la demande d'annulation du retour à l'abonnement standard")); }
        });
    };

    const handleCancelTerminationRequest = async() => {
        cancelTerminationRequest.mutate(undefined, {
            onSuccess: () => { toast(defaultSuccessToast("L'annulation de la résiliation a bien été prise en compte")); },
            onError: () => { toast(defaultErrorToast("Une erreur est survenue lors de la demande d'annulation de la résiliation")); }
        });
    };

   const handleChangeToUnlimitedPlan = async () => {
        upgradeToUnlimited.mutate(undefined, {
            onSuccess: () => {
                if ($ok(corporation?.mandate?.cardNumber)) {
                    startPendingPayment("upgradeToUnlimited");
                } else {
                    toast(defaultSuccessToast("Nous avons bien pris en compte votre demande de passer votre abonnement à l'illimité, celle-ci sera traitée dans les meilleurs délais"));
                }
            },
            onError: () => { handlePaymentError("upgradeToUnlimited"); }
        });
    };

    const handleDowngradeRenewalToStandardPlan = async () => {
        requestDowngradRenewalToStandardPlan.mutate(undefined, {
            onSuccess: () => { toast(defaultSuccessToast("La demande pour revenir à l'abonnement standard a bien été prise en compte")); },
            onError: () => { toast(defaultErrorToast("Une erreur est survenue lors de la demande pour revenir à l'abonnement stantard")); }
        });
    };

    const handleDownloadInvoice = async (orderId: UUID, invoiceReference: string) => {
        downloadInvoice.mutate({orderId, invoiceReference}, {
            onError: () => { toast(defaultErrorToast(`Une erreur est survenue lors de la récupération de la facture ${invoiceReference}`)); }
        });
    };

    const handleStartNow = async (unlimited: boolean) => {
        startSubscriptionImmediately.mutate(unlimited, {
            onSuccess: () => {
                if ($ok(corporation?.mandate?.cardNumber)) {
                    startPendingPayment("startSubscription");
                } else {
                    toast(defaultSuccessToast("Nous avons bien pris en compte votre demande de démarrer au plus tôt votre abonnement, celle-ci sera traitée dans les meilleurs délais"));
                }
            },
            onError: () => { handlePaymentError("startSubscription"); }
        });
    }

    const handleTerminate = async () => {
        requestCancelSubscription.mutate(undefined, {
            onSuccess: () => { toast(defaultSuccessToast("La demande de résiliation a bien été prise en compte")); },
            onError: () => { toast(defaultErrorToast("Une erreur est survenue lors de la résiliation de votre abonnement")); }
        });
    }

    function handleAddUsers(count: number): void {
    }

    function handleRemoveUsers(count: number): void {
    }

    return (
        <>
            <SeaShellBox component="form" px={4} py={10}>
                <SimpleGrid columns={{ base: 1, lg: 2}} spacing={5} mb={5}>
                    <PlanCard
                        connectedUser={connectedUser}
                        subscription={activeSubscription}
                        extraUsers={0}
                        productUnlimited={productUnlimited}
                        productCollab={productCollaborator}
                        productPack={productPack}
                        productStandard={productStandard}
                        isLoadingStartingNow={startSubscriptionImmediately.isLoading}
                        isLoadingBuyExtraPack={buyExtraPack.isLoading}
                        isLoadingUpgradeToUnlimited={upgradeToUnlimited.isLoading}
                        pendingOrder={pendingOrder}
                        onBuyExtraPack={handleBuyExtractPack}
                        onCancelDowngradingRequest={handleCancelDowngradingRequest}
                        onCancelTerminationRequest={handleCancelTerminationRequest}
                        onChangeToUnlimitedPlan={handleChangeToUnlimitedPlan}
                        onDowngradeRenewalToStandardPlan={handleDowngradeRenewalToStandardPlan}
                        onStartNow={handleStartNow}
                        onTerminate={handleTerminate}
                    />
                    <UsersCard
                        activeUsers={1}
                        maxUsers={maxUsers}
                        isTrialPeriod={isTrialPeriod}
                        pricePerExtraUser={productPack?.price ?? 0}
                        onAddUsers={handleAddUsers}
                        onRemoveUsers={handleRemoveUsers}
                    />
                    <MandateCard
                        mandate={mandate}
                        isCancelationRequested={$ok(activeSubscription?.requestCancelSubscription)}
                        companyName={config.app.title}
                        testMode={config.app.mollie.testmode}
                        mollie={mollie}
                        mollieReady={ready}
                        onCardSubmit={handleCardSubmit}
                        onMandateSubmit={handleMandateSubmit}
                    />
                </SimpleGrid>

                <OrdersCard orders={orders} onDownloadInvoice={handleDownloadInvoice} />
            </SeaShellBox>
            <WaitingModal isOpen={isPendingPayment} message={pendingMessage} onClose={() => setIsPendingPayment(false) } />
        </>
    );
};